import type { AssistantAnswerMode } from './incoming-calls/assistant-answer-mode';

/**
 * Settings › Calls & AI Assistant: which tab is open lives in `?tab=` so a tab can be linked to and the back
 * button works. A missing or unknown tab is left out of the search; the page picks the default once it knows
 * whether calls are live.
 */

export const CALLS_PAGE_TABS = ['results', 'flow', 'assistant'] as const;
export type CallsPageTab = (typeof CALLS_PAGE_TABS)[number];

const CALLS_PAGE_TABS_WITHOUT_AI: readonly CallsPageTab[] = CALLS_PAGE_TABS.filter((tab) => tab !== 'assistant');

export const CALLS_PAGE_TAB_LABELS: Record<CallsPageTab, string> = {
  results: 'Results',
  flow: 'Call flow',
  assistant: 'AI Assistant',
};

/** Tabs the page used to have, still found in bookmarks and older links: both now live in Call flow. */
const LEGACY_CALLS_PAGE_TABS: ReadonlyMap<string, CallsPageTab> = new Map<string, CallsPageTab>([
  ['ringing', 'flow'],
  ['texts', 'flow'],
]);

export type CallsPageSearch = { tab?: CallsPageTab };

/**
 * URL hash that lands on Call flow's "Text callers you miss" switch (`?tab=flow#call-flow-text-back`), used by the
 * Reports "Text back missed callers" link.
 */
export const CALL_FLOW_TEXT_BACK_ID = 'call-flow-text-back';

export function isCallsPageTab(value: unknown): value is CallsPageTab {
  return typeof value === 'string' && (CALLS_PAGE_TABS as readonly string[]).includes(value);
}

/** Route `validateSearch`: keeps only a known tab (an old tab name opens the tab that now holds it). */
export function parseCallsPageSearch(search: Record<string, unknown>): CallsPageSearch {
  const { tab } = search;
  if (isCallsPageTab(tab)) return { tab };
  const legacy = typeof tab === 'string' ? LEGACY_CALLS_PAGE_TABS.get(tab) : undefined;
  return legacy ? { tab: legacy } : {};
}

/**
 * The tabs a mode shows. AI Assistant only teaches the AI, so it shows while the AI can answer a call. The URL parser
 * above still accepts `assistant`, because the mode is unknown until the settings load.
 */
export function visibleCallsPageTabs(mode: AssistantAnswerMode): readonly CallsPageTab[] {
  return mode === 'MISSED' || mode === 'FIRST' ? CALLS_PAGE_TABS : CALLS_PAGE_TABS_WITHOUT_AI;
}

/** The open tab: the linked one, else Results once calls are live, else Call flow (set calls up first). */
export function resolveCallsPageTab(tab: CallsPageTab | undefined, isLive: boolean): CallsPageTab {
  return tab ?? (isLive ? 'results' : 'flow');
}
